import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Skeleton,
  SvgIconTypeMap,
} from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';

interface StatisticsCardProps {
  title: string;
  value: number | string;
  subtitle?: string;
  icon?: OverridableComponent<SvgIconTypeMap<{}, "svg">>;
  loading?: boolean;
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  trend?: {
    value: number;
    label: string;
  };
}

const StatisticsCard: React.FC<StatisticsCardProps> = ({
  title,
  value,
  subtitle,
  icon: Icon,
  loading = false,
  color = 'primary',
  trend,
}) => {
  const getColorPalette = (color: string) => {
    switch (color) {
      case 'success':
        return {
          bg: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
          text: '#ffffff',
        };
      case 'warning':
        return {
          bg: 'linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)',
          text: '#ffffff',
        };
      case 'error':
        return {
          bg: 'linear-gradient(135deg, #f44336 0%, #ef5350 100%)',
          text: '#ffffff',
        };
      case 'info':
        return {
          bg: 'linear-gradient(135deg, #2196f3 0%, #64b5f6 100%)',
          text: '#ffffff',
        };
      case 'secondary':
        return {
          bg: 'linear-gradient(135deg, #9c27b0 0%, #ba68c8 100%)',
          text: '#ffffff',
        };
      default: // primary
        return {
          bg: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
          text: '#ffffff',
        };
    }
  };

  const colors = getColorPalette(color);

  if (loading) {
    return (
      <Card
        sx={{
          height: '100%',
          background: colors.bg,
          color: colors.text,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          borderRadius: 3,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Skeleton variant="circular" width={48} height={48} sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
            <Box sx={{ ml: 2, flex: 1 }}>
              <Skeleton variant="text" width="60%" height={28} sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
            </Box>
          </Box>
          <Skeleton variant="text" width="40%" height={36} sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
          {subtitle && (
            <Skeleton variant="text" width="80%" height={20} sx={{ bgcolor: 'rgba(255,255,255,0.3)', mt: 1 }} />
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        height: '100%',
        background: colors.bg,
        color: colors.text,
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        borderRadius: 3,
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          {Icon && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: 'rgba(255,255,255,0.2)',
                mr: 2,
              }}
            >
              <Icon sx={{ fontSize: 24 }} />
            </Box>
          )}
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 500,
                fontSize: '0.875rem',
                opacity: 0.9,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              {title}
            </Typography>
          </Box>
        </Box>

        <Typography
          variant="h3"
          sx={{
            fontWeight: 'bold',
            fontSize: '2rem',
            mb: subtitle ? 1 : 0,
            lineHeight: 1.2,
          }}
        >
          {typeof value === 'number' ? value.toLocaleString() : value}
        </Typography>

        {subtitle && (
          <Typography
            variant="body2"
            sx={{
              opacity: 0.8,
              fontSize: '0.875rem',
            }}
          >
            {subtitle}
          </Typography>
        )}

        {trend && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.75rem',
                opacity: 0.9,
              }}
            >
              {trend.value > 0 ? '+' : ''}{trend.value}% {trend.label}
            </Typography>
          </Box>
        )}
      </CardContent>

      {/* Decorative element */}
      <Box
        sx={{
          position: 'absolute',
          top: -20,
          right: -20,
          width: 60,
          height: 60,
          borderRadius: '50%',
          backgroundColor: 'rgba(255,255,255,0.1)',
        }}
      />
    </Card>
  );
};

export default StatisticsCard;

